import PropTypes from 'prop-types'
import {
  TextField,
  Button,
  MenuItem,
  Typography,
  Box,
  IconButton,
  Divider,
  Grid,
  Switch,
  FormControlLabel,
} from '@mui/material'
import { Delete, Add } from '@mui/icons-material'

// Maybe export
const segmentTypes = [
  { id: 'polynomial', label: 'Polynomial' },
  { id: 'exponential', label: 'Exponential' },
]

const CurveFormFields = ({
  type,
  name,
  setName,
  description,
  setDescription,
  appliesTo,
  setAppliesTo,
  active,
  setActive,
  segments,
  handleSegmentChange,
  handleTermChange,
  handleAddSegment,
  handleRemoveSegment,
  handleAddTerm,
  handleRemoveTerm,
  handleSubmit,
}) => {
  return (
    <Box component="form" onSubmit={handleSubmit}>
      <TextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        required
      />

      <TextField
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        fullWidth
        required
      />

      <TextField
        select
        label="Applies To"
        value={appliesTo}
        onChange={(e) => setAppliesTo(e.target.value)}
        fullWidth
      >
        <MenuItem value="target">Target</MenuItem>
        <MenuItem value="shift">Shift</MenuItem>
      </TextField>

      <FormControlLabel
        control={
          <Switch checked={active} onChange={() => setActive(!active)} />
        }
        label="Active?"
        sx={{ display: type === 'create' ? 'none' : '' }}
      />

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>
        Segments
      </Typography>

      {segments.map((segment, index) => (
        <Box
          key={index}
          sx={{ border: '1px solid #ccc', p: 2, mb: 2, borderRadius: 2 }}
        >
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                label="Order"
                type="number"
                value={segment.segmentOrder ?? ''}
                onChange={(e) =>
                  handleSegmentChange(index, 'segmentOrder', +e.target.value)
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Start Knot"
                type="number"
                value={segment.startKnot ?? ''}
                onChange={(e) =>
                  handleSegmentChange(index, 'startKnot', +e.target.value)
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="End Knot"
                type="number"
                value={segment.endKnot ?? ''}
                onChange={(e) =>
                  handleSegmentChange(index, 'endKnot', +e.target.value)
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={1}>
              <IconButton onClick={() => handleRemoveSegment(index)}>
                <Delete />
              </IconButton>
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                label="Segment Type"
                value={segment.segmentType}
                onChange={(e) =>
                  handleSegmentChange(index, 'segmentType', e.target.value)
                }
                fullWidth
              >
                {segmentTypes.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {segment.segmentType === 'exponential' && (
              <>
                <Grid item xs={4}>
                  <TextField
                    label="a"
                    value={segment.a ?? ''}
                    onChange={(e) =>
                      handleSegmentChange(index, 'a', e.target.value)
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="b"
                    value={segment.b ?? ''}
                    onChange={(e) =>
                      handleSegmentChange(index, 'b', e.target.value)
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="c"
                    value={segment.c ?? ''}
                    onChange={(e) =>
                      handleSegmentChange(index, 'c', e.target.value)
                    }
                    fullWidth
                  />
                </Grid>
              </>
            )}

            {segment.segmentType === 'polynomial' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">Terms</Typography>
                  {segment.terms?.map((term, termIndex) => (
                    <Grid
                      container
                      spacing={2}
                      key={termIndex}
                      alignItems="center"
                    >
                      <Grid item xs={4}>
                        <TextField
                          label="Power"
                          type="number"
                          value={term.power ?? ''}
                          onChange={(e) =>
                            handleTermChange(
                              index,
                              termIndex,
                              'power',
                              +e.target.value
                            )
                          }
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Coefficient"
                          value={term.coefficient ?? ''}
                          onChange={(e) =>
                            handleTermChange(
                              index,
                              termIndex,
                              'coefficient',
                              e.target.value
                            )
                          }
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <IconButton
                          onClick={() => handleRemoveTerm(index, termIndex)}
                        >
                          <Delete />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                  <Button
                    startIcon={<Add />}
                    onClick={() => handleAddTerm(index)}
                    sx={{ mt: 1 }}
                  >
                    Add Term
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      ))}

      <Button startIcon={<Add />} onClick={() => handleAddSegment()}>
        Add Segment
      </Button>

      <Divider sx={{ my: 2 }} />

      <Button type="submit" variant="contained" color="primary">
        Save
      </Button>
    </Box>
  )
}

CurveFormFields.propTypes = {
  type: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  setName: PropTypes.func.isRequired,
  description: PropTypes.string.isRequired,
  setDescription: PropTypes.func.isRequired,
  appliesTo: PropTypes.string.isRequired,
  setAppliesTo: PropTypes.func.isRequired,
  active: PropTypes.bool.isRequired,
  setActive: PropTypes.func.isRequired,
  segments: PropTypes.array.isRequired,
  handleAddSegment: PropTypes.func.isRequired,
  handleSegmentChange: PropTypes.func.isRequired,
  handleTermChange: PropTypes.func.isRequired,
  handleRemoveSegment: PropTypes.func.isRequired,
  handleAddTerm: PropTypes.func.isRequired,
  handleRemoveTerm: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
}

export default CurveFormFields
